// VFS port of @archlang/lsp's package-loader. Same algorithm — manifest
// parse, recursive dep load with cycle detection, stdlib auto-load,
// .arch collection, resolvePackage. Only the I/O substrate is different:
// reads come from an in-memory Map instead of fs/promises.

import { parseCached, parsePackageManifest } from "@archlang/engine";
import {
  resolvePackage,
  parseMarkdownDocument,
  type Diagnostic,
  type MarkdownDocument,
  type PackageInput,
  type PackageManifest,
  type PackageMap,
  type ParsedFile,
  type QualifiedName,
  type ResolvedPackage,
  type SpaceScopedUses,
} from "@archlang/engine";
import { vfsJoin, vfsIsInside, isMarkdownPath, type Vfs } from "./vfs.js";

// Incremental parse (B2) lives in @archlang/engine as parseCached — a shared,
// content-addressed, deterministic cache so every loader (viewer-core, lsp,
// server, cli) skips re-parsing unchanged files. Re-export the reset hook so
// tests importing it from here keep working.
export { _resetParseCache } from "@archlang/engine";

// Resolved stdlib DEPENDENCY packages, shared across discovery calls. The stdlib
// is immutable per session — every board / editor compile passes the same
// `STDLIB_FILES` — so resolving `arch.backend` (and friends) into a fresh
// `LoadedPackage` for each new compiler is pure redundant work (~3.6 ms/compile
// for a small model, dominating a many-boards page and the editor's cold start).
// Resolve each stdlib package ONCE and reuse the `ResolvedPackage` — it is
// read-only reference data (symbol tables), the reuse the module doc-comment
// already anticipates. Keyed by the stdlib VFS IDENTITY (a stable constant
// reference; a distinct custom stdlib Map gets its own bucket, so a different
// stdlib is never cross-served) then the dep package's root path. A WeakMap so a
// dropped stdlib VFS stays collectable.
let sharedStdlibPackages = new WeakMap<Vfs, Map<string, LoadedPackage>>();

/** Test hook: drop the shared stdlib package cache (WeakMap can't be cleared, so
 *  swap in a fresh one). Pairs with `_resetParseCache` for a truly cold compile. */
export function _resetStdlibPackageCache(): void {
  sharedStdlibPackages = new WeakMap();
}

export interface LoadedPackage {
  readonly resolved: ResolvedPackage;
  readonly files: ReadonlyMap<string, ParsedFile>;
  readonly sources: ReadonlyMap<string, string>;
  readonly rootPath: string;
  readonly manifest?: PackageManifest;
  readonly dependencies: ReadonlyMap<string, LoadedPackage>;
}

export interface VfsLoadOptions {
  readonly userVfs: Vfs;
  readonly userRoot: string;
  readonly stdlibVfs: Vfs;
  readonly stdlibRoot: string;
}

interface LoadContext {
  readonly opts: VfsLoadOptions;
  readonly read: (path: string) => string | undefined;
  readonly list: (dir: string) => readonly { name: string; isDir: boolean }[];
  readonly resolving: Set<string>;
  readonly cache: Map<string, LoadedPackage>;
}

/** The discovery half of a package load: roots, dependency packages (resolved —
 *  they're external + immutable per session), file sources, and the
 *  space map — everything {@link IncrementalPackageCompiler.load} needs, WITHOUT
 *  resolving the user package itself. The incremental compiler owns that. */
export interface DiscoveredPackage {
  readonly input: PackageInput;
  /** Resolved dependency packages, name → ResolvedPackage. */
  readonly deps: PackageMap;
  readonly files: ReadonlyMap<string, ParsedFile>;
  readonly sources: ReadonlyMap<string, string>;
  readonly rootPath: string;
  readonly manifest?: PackageManifest;
  readonly dependencies: ReadonlyMap<string, LoadedPackage>;
  /** Discovery diagnostics (manifest/dependency/stdlib/cycle) — surfaced
   *  alongside the compiler's resolve/validate diagnostics. */
  readonly diagnostics: readonly Diagnostic[];
}

function makeContext(opts: VfsLoadOptions): LoadContext {
  const combined = new Map<string, string>();
  // Normalise every user VFS key to an absolute path. Discovery reads files
  // back by the absolute paths `vfsJoin` builds while walking the tree (e.g.
  // `/main.arch`), so a bare-keyed store (`main.arch`) would miss on `ctx.read`
  // and drop the file WHOLE (modules, views, everything) — silently. Hosts key
  // either way (the viewer uses `/`-prefixed paths, Studio keys bare), so make
  // both compile here rather than depending on each caller to pre-normalise.
  for (const [k, v] of opts.userVfs) combined.set(vfsJoin("/", k), v);
  for (const [k, v] of opts.stdlibVfs) {
    combined.set(vfsJoin(opts.stdlibRoot, k), v);
  }

  const childIndex = new Map<string, Map<string, boolean>>();
  for (const path of combined.keys()) {
    const parts = path.split("/").filter(Boolean);
    let dir = "/";
    for (let i = 0; i < parts.length; i++) {
      const name = parts[i]!;
      const isLast = i === parts.length - 1;
      let bucket = childIndex.get(dir);
      if (!bucket) {
        bucket = new Map();
        childIndex.set(dir, bucket);
      }
      // Once seen as a directory, never demote to file.
      if (!bucket.has(name) || bucket.get(name) === false) {
        bucket.set(name, !isLast || bucket.get(name) === false);
      }
      dir = dir === "/" ? `/${name}` : `${dir}/${name}`;
    }
  }

  return {
    opts,
    read: (p) => combined.get(p),
    list: (dir) => {
      const bucket = childIndex.get(dir);
      if (!bucket) return [];
      return [...bucket.entries()].map(([name, isDir]) => ({ name, isDir }));
    },
    resolving: new Set(),
    cache: new Map(),
  };
}

/** Discovery only — feeds the incremental compiler. The product path
 *  (browser, one-shot loads) uses this + `IncrementalPackageCompiler`; the cold
 *  `loadPackageFromVfs` below stays for the equivalence-test oracle. */
export async function discoverPackageFromVfs(opts: VfsLoadOptions): Promise<DiscoveredPackage> {
  return discoverInternal(opts.userRoot, makeContext(opts));
}

/** Cold full load (discovery + cold `resolvePackage`). Kept as the equivalence
 *  oracle the incremental path is checked against — not on any product path. */
export async function loadPackageFromVfs(opts: VfsLoadOptions): Promise<LoadedPackage> {
  return loadInternal(opts.userRoot, makeContext(opts));
}

/** Full load = discovery + cold resolve. Used for dependency packages (which
 *  must be resolved) and by the cold `loadPackageFromVfs` oracle. */
async function loadInternal(rootPath: string, ctx: LoadContext): Promise<LoadedPackage> {
  // A package under the stdlib root is immutable and identical across every
  // compile that shares this stdlib VFS — serve it from (and populate) the
  // cross-discovery shared cache. Everything else stays per-discovery.
  const stdlibScoped = vfsIsInside(rootPath, ctx.opts.stdlibRoot);
  const shared = stdlibScoped ? bucketFor(ctx.opts.stdlibVfs) : null;
  const hit = shared?.get(rootPath) ?? ctx.cache.get(rootPath);
  if (hit) return hit;
  const d = await discoverInternal(rootPath, ctx);
  const resolved = resolvePackage(d.input, d.deps);
  const loaded: LoadedPackage = {
    resolved: { ...resolved, diagnostics: [...d.diagnostics, ...resolved.diagnostics] },
    files: d.files,
    sources: d.sources,
    rootPath,
    manifest: d.manifest,
    dependencies: d.dependencies,
  };
  ctx.cache.set(rootPath, loaded);
  shared?.set(rootPath, loaded);
  return loaded;
}

/** The shared-cache bucket for a stdlib VFS, created on first use. */
function bucketFor(stdlibVfs: Vfs): Map<string, LoadedPackage> {
  let byPath = sharedStdlibPackages.get(stdlibVfs);
  if (!byPath) {
    byPath = new Map();
    sharedStdlibPackages.set(stdlibVfs, byPath);
  }
  return byPath;
}

/** Discovery: manifest, recursive dependency load (deps ARE resolved), stdlib
 *  auto-load, `.arch` collection + parse, space map. Stops short of resolving
 *  the package itself. */
async function discoverInternal(rootPath: string, ctx: LoadContext): Promise<DiscoveredPackage> {
  const diagnostics: Diagnostic[] = [];
  const manifestPath = vfsJoin(rootPath, "package.archspace");
  const manifestText = ctx.read(manifestPath);

  let manifest: PackageManifest | undefined;
  if (manifestText !== undefined) {
    const r = parsePackageManifest(manifestText, manifestPath);
    diagnostics.push(
      ...r.errors.map((e) => ({
        severity: "error" as const,
        message: e.message,
        source: e.span,
        code: "MANIFEST_PARSE_ERROR",
      }))
    );
    manifest = r.manifest;
  }

  if (manifest && manifest.package?.[0] === "arch") {
    const insideStdlib = vfsIsInside(rootPath, ctx.opts.stdlibRoot);
    if (!insideStdlib) {
      diagnostics.push({
        severity: "error",
        message: `Package name '${manifest.package.join(".")}' uses the reserved 'arch.*' prefix.`,
        source: manifest.packageSource ?? manifest.source,
        code: "RESERVED_PACKAGE_NAMESPACE",
      });
    }
  }

  if (ctx.resolving.has(rootPath)) {
    diagnostics.push({
      severity: "error",
      message: `Dependency cycle detected at ${rootPath}`,
      source: manifest?.source,
      code: "DEP_CYCLE",
    });
    return makeEmptyDiscovered(rootPath, manifest, diagnostics);
  }
  ctx.resolving.add(rootPath);

  const depMap = new Map<string, LoadedPackage>();

  for (const dep of manifest?.dependencies ?? []) {
    const depPath = vfsJoin(rootPath, dep.path);
    if (ctx.resolving.has(depPath)) {
      diagnostics.push({
        severity: "error",
        message: `Dependency cycle: '${dep.name.join(".")}' depends back on this package`,
        source: dep.source,
        code: "DEP_CYCLE",
      });
      continue;
    }
    try {
      const loaded = await loadInternal(depPath, ctx);
      depMap.set(dep.name.join("."), loaded);
      const declaredName = loaded.manifest?.package?.join(".");
      const expectedName = dep.name.join(".");
      if (declaredName && declaredName !== expectedName) {
        diagnostics.push({
          severity: "error",
          message: `Dependency name mismatch: ${expectedName} expected, found '${declaredName}' at ${depPath}`,
          source: dep.nameSource ?? dep.source,
          code: "DEP_NAME_MISMATCH",
        });
      }
      if (loaded.resolved.diagnostics.some((d) => d.code === "DEP_CYCLE")) {
        diagnostics.push({
          severity: "error",
          message: `Dependency cycle reaches this package via '${dep.name.join(".")}'`,
          source: dep.source,
          code: "DEP_CYCLE",
        });
      }
    } catch (err) {
      diagnostics.push({
        severity: "error",
        message: `Failed to load dependency '${dep.name.join(".")}' at ${depPath}: ${String(err)}`,
        source: dep.pathSource ?? dep.source,
        code: "DEP_LOAD_FAILED",
      });
    }
  }

  const stdlibUses = collectStdlibUses(manifest, []);
  for (const stdName of stdlibUses) {
    if (depMap.has(stdName)) continue;
    const stdPath = vfsJoin(ctx.opts.stdlibRoot, stdName);
    try {
      const loaded = await loadInternal(stdPath, ctx);
      depMap.set(stdName, loaded);
    } catch {
      diagnostics.push({
        severity: "error",
        message: `Std lib package '${stdName}' not found at ${stdPath}; check viewer-core stdlib bundle`,
        code: "STDLIB_NOT_FOUND",
      });
    }
  }

  const { files: archPaths, mdFiles, spaces: fileSpaces, spaceUses } = collectArchPaths(rootPath, ctx);
  const files = new Map<string, ParsedFile>();
  const sources = new Map<string, string>();
  // Parse markdown documents (§4.15) — verbatim prose + cross-references, no
  // canonical elements. A frontmatter `space:` overrides the path-derived one.
  const documents: MarkdownDocument[] = [];
  for (const abs of mdFiles) {
    const text = ctx.read(abs);
    if (text === undefined) continue;
    // Note: a .md is NOT added to `sources` — that map feeds the .arch parser.
    const doc = parseMarkdownDocument(abs, text);
    const space = doc.space ?? fileSpaces.get(abs);
    documents.push(space ? { ...doc, space } : doc);
  }
  for (const abs of archPaths) {
    const text = ctx.read(abs);
    if (text === undefined) continue;
    const r = parseCached(text, abs);
    files.set(abs, r.file);
    sources.set(abs, text);
    diagnostics.push(
      ...r.errors.map((e) => ({
        severity: "error" as const,
        message: e.message,
        source: e.span,
        code: "PARSE_ERROR",
      }))
    );

    for (const u of r.file.uses) {
      const fromName = u.fromPackage.join(".");
      if (!fromName.startsWith("arch.")) continue;
      if (depMap.has(fromName)) continue;
      const stdPath = vfsJoin(ctx.opts.stdlibRoot, fromName);
      try {
        const loaded = await loadInternal(stdPath, ctx);
        depMap.set(fromName, loaded);
      } catch {
        diagnostics.push({
          severity: "error",
          message: `Std lib package '${fromName}' not found at ${stdPath}`,
          source: u.fromSource ?? u.source,
          code: "STDLIB_NOT_FOUND",
        });
      }
    }
  }

  // Auto-load stdlib packages referenced only from a nested space manifest's
  // `use` (the per-file / root-manifest passes above never see those).
  for (const su of spaceUses) {
    for (const u of su.uses) {
      const fromName = u.fromPackage.join(".");
      if (!fromName.startsWith("arch.")) continue;
      if (depMap.has(fromName)) continue;
      const stdPath = vfsJoin(ctx.opts.stdlibRoot, fromName);
      try {
        depMap.set(fromName, await loadInternal(stdPath, ctx));
      } catch {
        diagnostics.push({
          severity: "error",
          message: `Std lib package '${fromName}' not found at ${stdPath}`,
          source: u.fromSource ?? u.source,
          code: "STDLIB_NOT_FOUND",
        });
      }
    }
  }

  const pkgInput: PackageInput = {
    name: manifest?.package,
    manifest,
    files: [...files.values()],
    rootPath,
    spaces: fileSpaces,
    spaceUses,
    documents,
  };
  const resolverDeps: PackageMap = new Map(
    [...depMap].map(([k, v]) => [k, v.resolved])
  );

  ctx.resolving.delete(rootPath);

  return {
    input: pkgInput,
    deps: resolverDeps,
    files,
    sources,
    rootPath,
    manifest,
    dependencies: depMap,
    diagnostics,
  };
}

function makeEmptyDiscovered(
  rootPath: string,
  manifest: PackageManifest | undefined,
  diagnostics: readonly Diagnostic[]
): DiscoveredPackage {
  return {
    input: { manifest, files: [], rootPath, name: manifest?.package },
    deps: new Map(),
    files: new Map(),
    sources: new Map(),
    rootPath,
    manifest,
    dependencies: new Map(),
    diagnostics,
  };
}

function collectArchPaths(
  rootPath: string,
  ctx: LoadContext
): {
  files: string[];
  mdFiles: string[];
  spaces: Map<string, QualifiedName>;
  spaceUses: SpaceScopedUses[];
} {
  const out: string[] = [];
  const mdOut: string[] = [];
  const spaces = new Map<string, QualifiedName>();
  // Space-scoped imports (§4.11): a nested `name:` manifest's `use` is visible
  // to that space's whole subtree. Collected here so the resolver can scope it.
  const spaceUses: SpaceScopedUses[] = [];
  const stack: { dir: string; space: QualifiedName | undefined }[] = [
    { dir: rootPath, space: undefined },
  ];
  while (stack.length > 0) {
    const { dir, space } = stack.pop()!;
    const entries = ctx.list(dir);
    let currentSpace = space;
    if (dir !== rootPath && entries.some((e) => !e.isDir && e.name === "package.archspace")) {
      const text = ctx.read(vfsJoin(dir, "package.archspace"));
      const m =
        text !== undefined
          ? parsePackageManifest(text, vfsJoin(dir, "package.archspace")).manifest
          : undefined;
      if (m?.package !== undefined) continue; // nested package — opaque.
      if (m?.spaceName !== undefined) {
        currentSpace = m.spaceName; // enter space.
        if (m.uses.length > 0) spaceUses.push({ space: m.spaceName, uses: m.uses });
      }
    }
    for (const e of entries) {
      if (e.name === "node_modules" || e.name === ".git" || e.name === "dist") continue;
      const abs = vfsJoin(dir, e.name);
      if (e.isDir) stack.push({ dir: abs, space: currentSpace });
      else if (e.name.endsWith(".arch")) {
        out.push(abs);
        if (currentSpace !== undefined) spaces.set(abs, currentSpace);
      } else if (isMarkdownPath(e.name)) {
        // Markdown documents are a first-class archspace surface (§4.15). They
        // declare no canonical model elements — just prose + cross-references.
        mdOut.push(abs);
        if (currentSpace !== undefined) spaces.set(abs, currentSpace);
      }
    }
  }
  return { files: out, mdFiles: mdOut, spaces, spaceUses };
}

function collectStdlibUses(
  manifest: PackageManifest | undefined,
  files: readonly ParsedFile[]
): string[] {
  const out = new Set<string>();
  const visit = (uses: readonly { fromPackage: QualifiedName }[]): void => {
    for (const u of uses) {
      const name = u.fromPackage.join(".");
      if (name.startsWith("arch.")) out.add(name);
    }
  };
  if (manifest) visit(manifest.uses);
  for (const f of files) visit(f.uses);
  return [...out];
}
